import React, { useEffect, useState, createElement } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
type FAQItem = {
  question: string;
  answer: string;
};
type FAQCategory = {
  title: string;
  eyebrow: string;
  items: FAQItem[];
};
const faqData: FAQCategory[] = [{
  eyebrow: '01',
  title: 'About 888 Sourcing Ltd',
  items: [{
    question: 'Who is 888 Sourcing Ltd, and what do you do?',
    answer: '888 Sourcing Ltd is an independent procurement consultancy and sourcing intermediary, registered in the United Kingdom (Companies House No. 08400329). We facilitate sourcing, supplier verification, tender coordination, and contract liaison for institutional and commercial buyers. We connect manufacturers, exporters, and distributors with procurement opportunities relating to multilateral, government-backed, and African Union (AU)-funded programmes across global markets, with a particular focus on Africa, Asia, and the Middle East.'
  }, {
    question: 'How do you engage with African Union (AU)-funded and multilateral procurement opportunities?',
    answer: '888 Sourcing Ltd operates as an independent procurement consultancy specialising in the sourcing and supplier-side coordination of opportunities relating to African Union (AU)-funded, multilateral, and government-backed programmes. We support suppliers and institutional buyers by facilitating supplier verification, tender preparation, documentation, and contract liaison across these procurement contexts. Our role is commercial and advisory, focused on connecting capable suppliers with relevant opportunities across global markets.'
  }, {
    question: 'Where are you based and which markets do you cover?',
    answer: 'Our headquarters is in London, United Kingdom. We operate globally across five continents, with established procurement networks in Africa (West, East, and Southern), Europe, Asia-Pacific, the Middle East, and the Americas. Africa is a flagship focus, alongside other high-growth emerging markets.'
  }]
}, {
  eyebrow: '02',
  title: 'Becoming a Supplier',
  items: [{
    question: 'Who can register as a supplier?',
    answer: 'We welcome registration from manufacturers, exporters, distributors, trading companies, and qualified service providers across our seven core sectors: construction materials, industrial equipment, medical supplies, agricultural products, ICT and telecommunications, energy and infrastructure, and consumer and commercial goods. Applicants should be able to demonstrate production or supply capability, regulatory compliance, and export readiness.'
  }, {
    question: 'What documents and information do I need to register?',
    answer: 'At the registration stage, we ask for your company profile, certificate of incorporation, key product or service catalogue, production or supply capacity, applicable certifications (such as ISO, CE, FDA, or sector-specific approvals), and references where available. Additional documentation may be requested at the verification or tender-engagement stage depending on the destination country, sector, and the specific procurement entity involved. Our team will guide you through any further requirements as opportunities progress.'
  }, {
    question: 'What happens after I submit the registration form?',
    answer: 'Our procurement team reviews each submission against our supplier verification criteria, which include capability, capacity, compliance, and commercial readiness. You will typically receive a response within five business days. If your profile aligns with current or upcoming procurement opportunities, we will be in touch to discuss next steps in detail.'
  }, {
    question: 'Does registration guarantee that I will receive contracts or opportunities?',
    answer: 'No. Registration adds your company to our supplier network for consideration, but it does not guarantee any specific procurement opportunity, contract, or commercial outcome. All opportunities are subject to client requirements, supplier suitability, and competitive evaluation processes.'
  }]
}, {
  eyebrow: '03',
  title: 'How We Work',
  items: [{
    question: 'How do you select and verify suppliers?',
    answer: 'Our verification process evaluates suppliers across four core criteria: capability (production or supply capacity and technical competence), compliance (regulatory, certification, and export readiness), commercial standing (financial reliability and track record), and capacity (ability to deliver at the scale and timeline required). Verified suppliers are matched to opportunities where their profile aligns with the technical, geographic, and commercial requirements of the procurement entity.'
  }, {
    question: 'What commercial terms and payment structures do you support?',
    answer: 'We work with all common international trade Incoterms including EXW, FOB, CIF, CFR, and DDP. Supported payment instruments include telegraphic transfer (T/T), letters of credit (L/C), documents against payment (D/P), and documents against acceptance (D/A). Specific terms are agreed per engagement based on client requirements, supplier capability, and risk profile.'
  }, {
    question: 'Do you handle logistics, shipping, and customs?',
    answer: 'Yes. We provide end-to-end logistics coordination including export documentation, freight forwarder selection, customs clearance, insurance arrangement, and in-country delivery management. We work with established freight and customs partners across all our operating regions.'
  }]
}, {
  eyebrow: '04',
  title: 'Procurement & Tenders',
  items: [{
    question: 'How are procurement opportunities identified and shared with suppliers?',
    answer: "Our team continuously monitors institutional, multilateral, and government procurement channels across our target markets. When an opportunity matches a registered supplier's capabilities, we contact the relevant suppliers directly with tender details, technical specifications, and submission timelines. We do not publish active tenders publicly on this website."
  }, {
    question: 'What if I do not have all the certifications required for a tender?',
    answer: 'Certification requirements vary significantly by sector and procurement context. If you do not currently hold a required certification, we can advise on pathways to compliance or, where appropriate, identify alternative tenders better suited to your current capabilities. Many of our suppliers have progressively built certification portfolios over time.'
  }]
}, {
  eyebrow: '05',
  title: 'Data Protection',
  items: [{
    question: 'How is my company data stored and protected?',
    answer: 'We handle all personal and business data in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018. Submitted information is stored securely and used only for supplier evaluation, procurement coordination, and communications you have consented to. Full details are set out in our Privacy Policy.'
  }, {
    question: 'Can I update or withdraw my supplier profile later?',
    answer: 'Yes. You can request updates, corrections, or withdrawal of your supplier profile at any time by contacting us at info@888sourcing.co.uk. Under UK GDPR, you also have the right to access, rectify, and erase your data, subject to legal retention obligations.'
  }]
}];
// Flatten for schema
const allFAQs = faqData.flatMap(cat => cat.items);
export function FAQ() {
  useSEO({
    title: 'Frequently Asked Questions',
    description: 'Answers to common questions about 888 Sourcing Ltd, becoming a supplier, our procurement process, commercial terms, and data protection.',
    path: '/faq'
  });
  // Inject FAQ schema for Google rich results
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allFAQs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    };
    let script = document.getElementById('faq-schema') as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = 'faq-schema';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
    return () => {
      const existing = document.getElementById('faq-schema');
      if (existing) existing.remove();
    };
  }, []);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const toggleItem = (key: string) => {
    setOpenItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);else next.add(key);
      return next;
    });
  };
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-451">
      {/* Hero Band */}
      <section className="py-16 px-6 bg-navy-light border-b border-slate-800" data-id="element-452">
        <div className="max-w-4xl mx-auto text-center" data-id="element-453">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-454">
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-455">
              <div className="w-12 h-px bg-gold" data-id="element-456"></div>
              <span className="text-gold text-xs font-semibold tracking-widest uppercase" data-id="element-457">
                Resources
              </span>
              <div className="w-12 h-px bg-gold" data-id="element-458"></div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-semibold text-white mb-6" data-id="element-459">
              Frequently Asked Questions
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed" data-id="element-460">
              Answers to common questions from suppliers, manufacturers, and
              procurement partners.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 px-6" data-id="element-461">
        <div className="max-w-3xl mx-auto space-y-16" data-id="element-462">
          {faqData.map((category, catIndex) => <motion.div key={category.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: catIndex * 0.05
        }} data-id="element-463">
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6" data-id="element-464">
                <span className="text-gold text-sm font-bold" data-id="element-465">
                  {category.eyebrow}
                </span>
                <div className="w-8 h-px bg-gold" data-id="element-466"></div>
                <h2 className="text-2xl font-semibold text-white" data-id="element-467">
                  {category.title}
                </h2>
              </div>

              {/* Accordion */}
              <div className="space-y-3" data-id="element-468">
                {category.items.map((item, itemIndex) => {
              const key = `${catIndex}-${itemIndex}`;
              const isOpen = openItems.has(key);
              return <div key={key} className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden transition-colors hover:border-slate-700" data-id="element-469">
                      <button onClick={() => toggleItem(key)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left focus:outline-none focus:ring-2 focus:ring-gold/40" aria-expanded={isOpen} data-id="element-470">
                        <span className="text-white font-medium" data-id="element-471">
                          {item.question}
                        </span>
                        <div className="flex-shrink-0 w-8 h-8 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center" data-id="element-472">
                          {isOpen ? <MinusIcon size={16} className="text-gold" data-id="element-473" /> : <PlusIcon size={16} className="text-gold" data-id="element-474" />}
                        </div>
                      </button>
                      <AnimatePresence initial={false} data-id="element-475">
                        {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="overflow-hidden" data-id="element-476">
                            <div className="px-6 pb-5 pt-1 text-slate-400 leading-relaxed border-t border-slate-800/50" data-id="element-477">
                              {item.answer}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
            })}
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6" data-id="element-478">
        <div className="max-w-3xl mx-auto" data-id="element-479">
          <div className="bg-navy-light border-l-4 border-gold p-8 rounded-lg text-center" data-id="element-480">
            <h2 className="text-2xl font-semibold text-white mb-3" data-id="element-481">
              Still have questions?
            </h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto" data-id="element-482">
              Our team is happy to help. Get in touch or register your company
              to start the conversation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-483">
              <Link to="/contact" className="bg-gold hover:bg-gold-dark text-navy-dark px-6 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-484">
                Contact Us
              </Link>
              <Link to="/supplier-registration" className="border-2 border-slate-700 hover:border-gold text-white px-6 py-3 rounded-md font-semibold text-sm transition-colors" data-id="element-485">
                Register as a Supplier
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}